import { Delaunay } from 'd3-delaunay';
import { clamp01 } from '../../core/math';
import { resolvePalette, type ResolvedPalette } from '../../core/palette';
import { generatePoints, type PointDistribution } from '../../core/pointSources';
import type { RenderTarget, TechniqueContext } from '../types';
import type { DelaunayValues } from './params';

/**
 * Delaunay triangulation of a point set.
 *
 * The triangle list comes straight from d3-delaunay; everything interesting here is in
 * the filtering. Long edges are what make a naive triangulation look like a spider's
 * web — filtering by edge length turns the same point set into a mesh that follows the
 * letterforms.
 */
export interface DelaunayState {
  points: Float64Array;
  /** Triangle vertex indices, three per triangle. */
  triangles: Uint32Array;
  /** Per-triangle centroid distance to the text, area and longest edge. */
  centroidDistance: Float32Array;
  areas: Float32Array;
  longestEdge: Float32Array;
  tint: Float32Array;
  palette: ResolvedPalette;
  built: boolean;
}

function values(ctx: TechniqueContext): DelaunayValues {
  return ctx.values as unknown as DelaunayValues;
}

export function init(ctx: TechniqueContext): DelaunayState {
  const v = values(ctx);
  const flat = generatePoints(v.points, v.distribution as PointDistribution, {
    field: ctx.field,
    width: ctx.width,
    height: ctx.height,
    rng: ctx.rng,
    distance: v.distributionDistance,
  });
  const points = new Float64Array(flat.length);
  points.set(flat);

  return {
    points,
    triangles: new Uint32Array(0),
    centroidDistance: new Float32Array(0),
    areas: new Float32Array(0),
    longestEdge: new Float32Array(0),
    tint: new Float32Array(0),
    palette: resolvePalette(v.palette, ctx.rng.fork(1)),
    built: false,
  };
}

export function step(state: DelaunayState, _dt: number, ctx: TechniqueContext): DelaunayState {
  if (state.built) return state;
  if (state.points.length < 6) {
    state.built = true;
    return state;
  }

  const delaunay = new Delaunay(state.points);
  state.triangles = Uint32Array.from(delaunay.triangles);

  const count = state.triangles.length / 3;
  state.centroidDistance = new Float32Array(count);
  state.areas = new Float32Array(count);
  state.longestEdge = new Float32Array(count);
  state.tint = new Float32Array(count);

  for (let t = 0; t < count; t++) {
    const a = state.triangles[t * 3];
    const b = state.triangles[t * 3 + 1];
    const c = state.triangles[t * 3 + 2];
    const ax = state.points[a * 2];
    const ay = state.points[a * 2 + 1];
    const bx = state.points[b * 2];
    const by = state.points[b * 2 + 1];
    const cx = state.points[c * 2];
    const cy = state.points[c * 2 + 1];

    state.centroidDistance[t] = ctx.field.distance((ax + bx + cx) / 3, (ay + by + cy) / 3);
    state.areas[t] = Math.abs((bx - ax) * (cy - ay) - (cx - ax) * (by - ay)) / 2;
    state.longestEdge[t] = Math.max(Math.hypot(bx - ax, by - ay), Math.hypot(cx - bx, cy - by), Math.hypot(ax - cx, ay - cy));
    state.tint[t] = ctx.rng.next();
  }

  state.built = true;
  return state;
}

export function isComplete(state: DelaunayState): boolean {
  return state.built;
}

export function draw(state: DelaunayState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  const c = target.ctx;
  const reach = Math.max(1, ctx.field.maxOutside);
  const [minEdge, maxEdge] = v.edgeLength;
  let maxArea = 1;
  for (const a of state.areas) if (a > maxArea) maxArea = a;

  c.lineWidth = v.lineWidth;
  c.lineJoin = 'round';

  const count = state.triangles.length / 3;
  for (let t = 0; t < count; t++) {
    const edge = state.longestEdge[t];
    if (edge < minEdge || edge > maxEdge) continue;

    const distance = state.centroidDistance[t];
    if (v.clip === 'outside' && distance < 0) continue;
    if (v.clip === 'inside' && distance > 0) continue;

    let tint: number;
    switch (v.colorMode) {
      case 'distance':
        tint = clamp01(Math.abs(distance) / reach);
        break;
      case 'size':
        tint = clamp01(state.areas[t] / maxArea);
        break;
      case 'random':
        tint = state.tint[t];
        break;
      default:
        tint = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0, v.opacity) : state.palette.at(tint, v.opacity);

    const a = state.triangles[t * 3];
    const b = state.triangles[t * 3 + 1];
    const cc = state.triangles[t * 3 + 2];
    const path = new Path2D();
    path.moveTo(state.points[a * 2], state.points[a * 2 + 1]);
    path.lineTo(state.points[b * 2], state.points[b * 2 + 1]);
    path.lineTo(state.points[cc * 2], state.points[cc * 2 + 1]);
    path.closePath();

    if (v.style === 'filled' || v.style === 'both') {
      c.fillStyle = color;
      c.fill(path);
    }
    if (v.style === 'wireframe' || v.style === 'both') {
      c.strokeStyle = color;
      c.stroke(path);
    }
  }
}

export function elementCount(state: DelaunayState): number {
  return state.triangles.length / 3;
}

export function toSVG(state: DelaunayState, ctx: TechniqueContext): string {
  const v = values(ctx);
  const reach = Math.max(1, ctx.field.maxOutside);
  const [minEdge, maxEdge] = v.edgeLength;
  let maxArea = 1;
  for (const a of state.areas) if (a > maxArea) maxArea = a;
  const f = (n: number): string => n.toFixed(2).replace(/\.?0+$/, '');
  const parts: string[] = [`<g fill-opacity="${v.opacity}" stroke-opacity="${v.opacity}" stroke-width="${v.lineWidth}">`];

  const count = state.triangles.length / 3;
  for (let t = 0; t < count; t++) {
    const edge = state.longestEdge[t];
    if (edge < minEdge || edge > maxEdge) continue;
    const distance = state.centroidDistance[t];
    if (v.clip === 'outside' && distance < 0) continue;
    if (v.clip === 'inside' && distance > 0) continue;

    let tint: number;
    switch (v.colorMode) {
      case 'distance':
        tint = clamp01(Math.abs(distance) / reach);
        break;
      case 'size':
        tint = clamp01(state.areas[t] / maxArea);
        break;
      case 'random':
        tint = state.tint[t];
        break;
      default:
        tint = 0;
    }
    const color = v.colorMode === 'solid' ? state.palette.stop(0) : state.palette.at(tint);
    const a = state.triangles[t * 3];
    const b = state.triangles[t * 3 + 1];
    const cc = state.triangles[t * 3 + 2];
    const pts = `${f(state.points[a * 2])},${f(state.points[a * 2 + 1])} ${f(state.points[b * 2])},${f(state.points[b * 2 + 1])} ${f(state.points[cc * 2])},${f(state.points[cc * 2 + 1])}`;
    parts.push(
      `<polygon points="${pts}" fill="${v.style === 'wireframe' ? 'none' : color}" stroke="${v.style === 'filled' ? 'none' : color}"/>`,
    );
  }

  parts.push('</g>');
  return parts.join('\n');
}
